import {HistoryIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Label, RelativeTime} from '@primer/react'
import {useState} from 'react'

import {useScribeArtifact} from '../hooks'
import type {ScribeArtifactResourceId, ScribeArtifactResourceType, ScribeArtifactVersion} from '../types'
import styles from './ArtifactHistoryMenu.module.css'

const versionDateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

type ArtifactHistoryMenuProps = {
  resourceType: ScribeArtifactResourceType
  resourceId: ScribeArtifactResourceId
  number: number
  selectedVersion: ScribeArtifactVersion | null
  onSelect: (version: ScribeArtifactVersion) => void
}

export function ArtifactHistoryMenu({
  resourceType,
  resourceId,
  number,
  selectedVersion,
  onSelect,
}: ArtifactHistoryMenuProps) {
  const [open, setOpen] = useState(false)

  return (
    <ActionMenu open={open} onOpenChange={setOpen}>
      <ActionMenu.Button variant="invisible" size="small" className={styles.HistoryButton} leadingVisual={HistoryIcon}>
        Edit history
      </ActionMenu.Button>
      <ActionMenu.Overlay width="medium">
        {open && (
          <ArtifactHistoryMenuContent
            resourceType={resourceType}
            resourceId={resourceId}
            number={number}
            selectedVersion={selectedVersion}
            onSelect={onSelect}
          />
        )}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function ArtifactHistoryMenuContent({
  resourceType,
  resourceId,
  number,
  selectedVersion,
  onSelect,
}: ArtifactHistoryMenuProps) {
  const {data, isError, isPending} = useScribeArtifact({resourceType, resourceId, number})
  const versions = data?.versions ?? []
  const statusMessage = isPending
    ? 'Loading edit history…'
    : isError
      ? 'Unable to load edit history'
      : versions.length === 0
        ? 'No version history available'
        : null

  return (
    <ActionList className={styles.HistoryList} selectionVariant={statusMessage ? undefined : 'single'}>
      <ActionList.Group>
        {!statusMessage && (
          <ActionList.GroupHeading>{`${versions.length} version${versions.length === 1 ? '' : 's'}`}</ActionList.GroupHeading>
        )}
        {statusMessage ? (
          <ActionList.Item key="initial-focus-target" disabled>
            {statusMessage}
          </ActionList.Item>
        ) : (
          versions.map((version, index) => {
            const isCurrent = index === 0
            const createdAt = version.created_at ? new Date(version.created_at) : null
            const validCreatedAt = createdAt && !Number.isNaN(createdAt.getTime()) ? createdAt : null
            const versionLabel = validCreatedAt
              ? versionDateFormatter.format(validCreatedAt)
              : `Version ${version.version}`

            return (
              <ActionList.Item
                key={isCurrent ? 'initial-focus-target' : version.version}
                selected={selectedVersion?.version === version.version || (!selectedVersion && isCurrent)}
                onSelect={() => onSelect(version)}
              >
                <div className={styles.HistoryVersionContent}>
                  <span className={styles.HistoryVersionLabel}>
                    {validCreatedAt ? (
                      <time dateTime={version.created_at ?? undefined}>{versionLabel}</time>
                    ) : (
                      versionLabel
                    )}
                  </span>
                  <span className={styles.HistoryVersionMetadata}>
                    <span>{version.actor?.login ?? 'Unknown user'}</span>
                    {validCreatedAt && (
                      <>
                        <span aria-hidden="true">·</span>
                        <RelativeTime date={validCreatedAt} />
                        <span aria-hidden="true">·</span>
                        <span>{`Version ${version.version}`}</span>
                      </>
                    )}
                  </span>
                </div>
                {isCurrent && (
                  <ActionList.TrailingVisual>
                    <Label>Current</Label>
                  </ActionList.TrailingVisual>
                )}
              </ActionList.Item>
            )
          })
        )}
      </ActionList.Group>
    </ActionList>
  )
}
